import { Textarea } from "@/components/ui/textarea";

export type SvrInventoryTeamValue = {
  inventoryTeamRequested: boolean;
  inventoryTeamMessage: string;
};

type Props = {
  value: SvrInventoryTeamValue;
  onChange: (next: SvrInventoryTeamValue) => void;
  disabled?: boolean;
  /** Compact styling for sheets / side panels */
  compact?: boolean;
};

/**
 * Legacy Inventory Team checkbox + message. Retired from SVR create/edit when Bring list shipped.
 * Kept so historical visits can still be read and server validation remains available.
 */
export function SvrInventoryTeamFields({ value, onChange, disabled, compact }: Props) {
  const requested = value.inventoryTeamRequested;
  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          className="mt-0.5 w-4 h-4 rounded accent-amber-500"
          checked={requested}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              inventoryTeamRequested: e.target.checked,
              inventoryTeamMessage: e.target.checked ? value.inventoryTeamMessage : "",
            })
          }
        />
        <span>
          <span className="text-sm font-medium">Inventory Team</span>
          <span className="block text-xs text-muted-foreground mt-0.5">
            Notify all active Inventory Team members by email. Message is staff-only and never shown to customers.
          </span>
        </span>
      </label>
      {requested && (
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
            Message to Inventory Team <span className="text-red-500">*</span>
          </p>
          <Textarea
            value={value.inventoryTeamMessage}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                inventoryTeamRequested: true,
                inventoryTeamMessage: e.target.value,
              })
            }
            placeholder="What to prepare, parts to bring, staging notes…"
            rows={compact ? 3 : 4}
            maxLength={4000}
            className="text-sm"
          />
          <p className="text-[10px] text-muted-foreground mt-1 text-right">
            {value.inventoryTeamMessage.trim().length} / 4000
          </p>
        </div>
      )}
    </div>
  );
}

/** Client-side validation before create/update mutate. */
export function validateSvrInventoryTeamSelection(value: SvrInventoryTeamValue): string | null {
  if (!value.inventoryTeamRequested) return null;
  if (!value.inventoryTeamMessage.trim()) {
    return "Message to Inventory Team is required when Inventory Team is selected.";
  }
  return null;
}

/** Surface partial staff email delivery from mutation results. */
export function toastSvrNotificationWarnings(
  toast: { warning: (msg: string) => void; error?: (msg: string) => void },
  warnings: string[] | null | undefined,
) {
  if (!warnings?.length) return;
  const shown = warnings.slice(0, 3);
  for (const w of shown) toast.warning(w);
  if (warnings.length > 3) {
    toast.warning(`${warnings.length - 3} more staff notification warning(s). Check Communications Log.`);
  }
}
